import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useConfig } from "../lib/useConfig";
import { useLang } from "../lib/i18n";
import { PageHeader, ROLE_LABELS } from "../lib/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PurgePanel } from "../components/PurgePanel";
import { UserPlus, Plug, History, Users as UsersIcon, MapPinned, Tag, Loader2, RefreshCw, Plus, Trash2, Mail, CalendarClock, Send, SlidersHorizontal, Database, Download, Pencil, UploadCloud, ShieldCheck, BarChart3 } from "lucide-react";

export default function Administration() {
  const qc = useQueryClient();
  const { data: cfg } = useConfig();
  const { t } = useLang();
  const [newUser, setNewUser] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data: users } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const { data: logs } = useQuery({ queryKey: ["audit"], queryFn: async () => (await api.get("/audit-logs")).data });

  const createUser = async () => {
    try {
      if (newUser.id) {
        const { id, ...body } = newUser;
        if (!body.password) delete body.password;
        await api.patch(`/users/${id}`, body);
        toast.success(t("Utilisateur mis à jour."));
      } else {
        await api.post("/users", newUser);
        toast.success(t("Utilisateur créé."));
      }
      qc.invalidateQueries({ queryKey: ["users"] });
      setNewUser(null);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const uploadUserPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNewUser((u) => ({ ...u, photo_path: r.data.path })); toast.success(t("Photo ajoutée."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    e.target.value = "";
  };

  const toggleActive = async (u) => {
    await api.patch(`/users/${u.id}`, { active: !u.active });
    qc.invalidateQueries({ queryKey: ["users"] });
  };

  const geo = cfg?.geo || {};

  return (
    <div data-testid="admin-page">
      <PageHeader title={t("Administration")} subtitle={t("Utilisateurs, unités géographiques, données de référence et intégrations")} />
      <Tabs defaultValue="users">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="tab-users"><UsersIcon className="h-4 w-4 mr-1" /> {t("Utilisateurs")}</TabsTrigger>
          <TabsTrigger value="geo" data-testid="tab-geo"><MapPinned className="h-4 w-4 mr-1" /> {t("Géographie")}</TabsTrigger>
          <TabsTrigger value="ref" data-testid="tab-ref"><Tag className="h-4 w-4 mr-1" /> {t("Données de référence")}</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations"><Plug className="h-4 w-4 mr-1" /> {t("Intégrations")}</TabsTrigger>
          <TabsTrigger value="thresholds" data-testid="tab-thresholds"><SlidersHorizontal className="h-4 w-4 mr-1" /> {t("Seuils des signaux")}</TabsTrigger>
          <TabsTrigger value="visibility" data-testid="tab-visibility"><ShieldCheck className="h-4 w-4 mr-1" /> {t("Confidentialité")}</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><History className="h-4 w-4 mr-1" /> {t("Journal d'audit")}</TabsTrigger>
          <TabsTrigger value="danger" data-testid="tab-danger"><Trash2 className="h-4 w-4 mr-1" /> {t("Maintenance")}</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="flex flex-wrap justify-end gap-2 mb-3">
            <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-users-open"><UsersIcon className="h-4 w-4 mr-1" /> {t("Créer plusieurs comptes")}</Button>
            <Button className="bg-[#F05A28] hover:bg-[#D84A1B]" onClick={() => setNewUser({ name: "", email: "", password: "", role: "field_epi", organization: "FHI 360", province: "", health_zone: "" })} data-testid="add-user"><UserPlus className="h-4 w-4 mr-1" /> {t("Nouvel utilisateur")}</Button>
          </div>
          {bulkOpen && <BulkUsersPanel onClose={() => setBulkOpen(false)} />}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>{t("Nom")}</TableHead><TableHead>{t("Adresse e-mail")}</TableHead><TableHead>{t("Rôle")}</TableHead><TableHead>{t("Province")}</TableHead><TableHead>{t("Zone")}</TableHead><TableHead>{t("Actif")}</TableHead><TableHead className="text-right">{t("Actions")}</TableHead></TableRow></TableHeader>
              <TableBody>{(users || []).map((u) => (
                <TableRow key={u.id}><TableCell className="font-medium">{u.name}{u.matricule ? <span className="block text-[11px] text-slate-500 font-mono">{u.matricule}</span> : null}</TableCell><TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                  <TableCell className="text-sm">{t(ROLE_LABELS[u.role] || u.role)}</TableCell><TableCell className="text-sm">{u.province || "-"}</TableCell>
                  <TableCell className="text-sm">{u.health_zone || "-"}</TableCell>
                  <TableCell><Switch checked={u.active !== false} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-${u.id}`} /></TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" data-testid={`edit-${u.id}`} onClick={() => setNewUser({ id: u.id, name: u.name || "", email: u.email || "", password: "", role: u.role, organization: u.organization || "FHI 360", province: u.province || "", health_zone: u.health_zone || "", matricule: u.matricule || "", phone: u.phone || "", job_title: u.job_title || "", project: u.project || "", point_focal: u.point_focal || "", photo_path: u.photo_path || "" })}><Pencil className="h-4 w-4" /></Button></TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="geo">
          <GeoManager geo={geo} />
        </TabsContent>

        <TabsContent value="ref">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <RefCard title={t("Types d'activités")} items={(cfg?.activities || []).map((a) => a.name)} />
            <RefCard title={t("Catégories de défis")} items={(cfg?.challenge_categories || []).map((a) => a.name)} />
            <RefCard title={t("Catégories de constats")} items={(cfg?.finding_categories || []).map((a) => a.name)} />
            <RefCard title={t("Indicateurs chiffrés")} items={(cfg?.metrics || []).map((a) => a.name)} />
            <RefCard title={t("Maladies / événements")} items={(cfg?.diseases || []).map((d) => t(d))} />
            <RefCard title={t("Rôles utilisateurs")} items={Object.values(cfg?.roles || {})} />
          </div>
        </TabsContent>

        <TabsContent value="integrations">
          <div className="grid lg:grid-cols-2 gap-5">
            <KoboPanel />
            <DigestSchedule />
            <Dhis2Panel />
            <PowerBiPanel />
            <EmailTestCard />
          </div>
          <p className="text-xs text-slate-500 mt-3">{t("L'architecture est modulaire et prête pour l'intégration de piliers additionnels (RCCE, PCI, EDS, etc.).")}</p>
        </TabsContent>

        <TabsContent value="thresholds">
          <ThresholdsPanel />
        </TabsContent>

        <TabsContent value="visibility">
          <VisibilityPanel />
        </TabsContent>

        <TabsContent value="danger">
          <PurgePanel />
        </TabsContent>

        <TabsContent value="audit">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>{t("Date")}</TableHead><TableHead>{t("Action")}</TableHead><TableHead>{t("Entité")}</TableHead><TableHead>{t("Utilisateur")}</TableHead><TableHead>{t("Détail")}</TableHead></TableRow></TableHeader>
              <TableBody>{(logs || []).map((l) => (
                <TableRow key={l.id}><TableCell className="font-mono text-xs">{l.at?.slice(0, 19).replace("T", " ")}</TableCell>
                  <TableCell className="text-sm">{l.action}</TableCell><TableCell className="text-sm">{l.entity}</TableCell>
                  <TableCell className="text-sm">{l.user_name}</TableCell><TableCell className="text-xs text-slate-500">{l.detail}</TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!newUser} onOpenChange={(o) => !o && setNewUser(null)}>
        <DialogContent className="max-w-lg">
          {newUser && (<>
            <DialogHeader><DialogTitle>{t(newUser.id ? "Modifier l'utilisateur" : "Nouvel utilisateur")}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>{t("Nom complet")}</Label><Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="nu-name" /></div>
              <div><Label>{t("Adresse e-mail")}</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="nu-email" /></div>
              <div><Label>{t("Mot de passe")}</Label><Input type="text" placeholder={newUser.id ? t("laisser vide pour conserver") : ""} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="nu-password" /></div>
              <div><Label>{t("Rôle")}</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger data-testid="nu-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(cfg?.roles || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("Province")}</Label>
                <Select value={newUser.province || "none"} onValueChange={(v) => setNewUser({ ...newUser, province: v === "none" ? "" : v, health_zone: "" })}>
                  <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">-</SelectItem>{Object.keys(geo).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>{t("Zone de Santé")}</Label>
                <Select value={newUser.health_zone || "none"} onValueChange={(v) => setNewUser({ ...newUser, health_zone: v === "none" ? "" : v })} disabled={!newUser.province}>
                  <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">-</SelectItem>{(newUser.province ? Object.values(geo[newUser.province]?.territories || {}).flat() : []).map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("Matricule")}</Label><Input value={newUser.matricule || ""} onChange={(e) => setNewUser({ ...newUser, matricule: e.target.value })} data-testid="nu-matricule" /></div>
              <div><Label>{t("Téléphone")}</Label><Input value={newUser.phone || ""} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} data-testid="nu-phone" /></div>
              <div><Label>{t("Fonction / poste")}</Label><Input value={newUser.job_title || ""} onChange={(e) => setNewUser({ ...newUser, job_title: e.target.value })} data-testid="nu-job" /></div>
              <div><Label>{t("Projet")}</Label><Input value={newUser.project || ""} onChange={(e) => setNewUser({ ...newUser, project: e.target.value })} data-testid="nu-project" /></div>
              <div className="col-span-2"><Label>{t("Point focal / superviseur")}</Label><Input value={newUser.point_focal || ""} onChange={(e) => setNewUser({ ...newUser, point_focal: e.target.value })} data-testid="nu-pf" /></div>
              <div className="col-span-2"><Label>{t("Photo")}</Label>
                <div className="flex items-center gap-3 mt-1">
                  {newUser.photo_path && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/mr-files/${newUser.photo_path}?auth=${localStorage.getItem("token")}`} alt="" className="h-12 w-12 rounded-lg object-cover bg-slate-100" />}
                  <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F05A28] cursor-pointer" data-testid="nu-photo"><UploadCloud className="h-4 w-4" /> {t("Téléverser")}<input type="file" accept="image/*" className="hidden" onChange={uploadUserPhoto} /></label>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setNewUser(null)}>{t("Annuler")}</Button><Button className="bg-[#F05A28] hover:bg-[#D84A1B]" onClick={createUser} data-testid="nu-save">{t(newUser.id ? "Enregistrer" : "Créer")}</Button></div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const RefCard = ({ title, items }) => (
  <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
    <h3 className="text-sm font-semibold text-slate-800 mb-3">{title}</h3>
    <div className="flex flex-wrap gap-1.5">{items.map((i) => <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{i}</span>)}</div>
  </div>
);

const THRESHOLD_FIELDS = [
  { key: "cfr_pct", label: "Létalité (CFR) : seuil Urgent (%)" },
  { key: "cfr_min_cases", label: "CFR : minimum de cas suspects" },
  { key: "cfr_min_deaths", label: "CFR : minimum de décès" },
  { key: "mortality_deaths", label: "Mortalité inhabituelle : décès par zone" },
  { key: "zone_surge_pct", label: "Hausse par zone : seuil (%)" },
  { key: "zone_surge_min", label: "Hausse par zone : minimum requis" },
  { key: "nat_alerts_urgent_pct", label: "Alertes nationales : seuil Urgent (%)" },
  { key: "nat_cases_urgent_pct", label: "Cas nationaux : seuil Urgent (%)" },
  { key: "low_comp_pct", label: "Complétude basse : seuil (%)" },
];

function ThresholdsPanel() {
  const qc = useQueryClient();
  const { t } = useLang();
  const { data } = useQuery({ queryKey: ["thresholds"], queryFn: async () => (await api.get("/signal-thresholds")).data });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (data && !form) setForm({ ...data }); }, [data]);
  if (!form) return null;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/signal-thresholds", Object.fromEntries(THRESHOLD_FIELDS.map((f) => [f.key, Number(form[f.key])])));
      toast.success(t("Seuils enregistrés."));
      qc.invalidateQueries({ queryKey: ["thresholds"] });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 max-w-3xl" data-testid="thresholds-panel">
      <div className="flex items-center gap-2 mb-1"><SlidersHorizontal className="h-4 w-4 text-[#F05A28]" /><h3 className="font-semibold text-slate-900">{t("Seuils de détection des signaux")}</h3></div>
      <p className="text-xs text-slate-500 mb-5">{t("Ajustez les seuils utilisés par la détection automatique des signaux et les alertes par e-mail.")}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {THRESHOLD_FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-xs">{t(f.label)}</Label>
            <Input type="number" min="0" step="any" value={form[f.key]} data-testid={`th-${f.key}`}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
          </div>
        ))}
      </div>
      <Button className="bg-[#F05A28] hover:bg-[#D84A1B] mt-5" onClick={save} disabled={busy} data-testid="th-save">{t("Enregistrer les seuils")}</Button>
    </div>
  );
}

function VisibilityPanel() {
  const qc = useQueryClient();
  const { t } = useLang();
  const { data } = useQuery({ queryKey: ["visibility"], queryFn: async () => (await api.get("/visibility-settings")).data });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (data && !form) setForm({ zone_team: data.zone_team, restricted_pillars: data.restricted_pillars }); }, [data, form]);
  if (!form || !data) return null;

  const toggle = (code) => setForm({
    ...form,
    restricted_pillars: form.restricted_pillars.includes(code)
      ? form.restricted_pillars.filter((c) => c !== code)
      : [...form.restricted_pillars, code],
  });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/visibility-settings", form);
      toast.success(t("Règles de confidentialité enregistrées."));
      qc.invalidateQueries({ queryKey: ["visibility"] });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 max-w-3xl" data-testid="visibility-panel">
      <div className="flex items-center gap-2 mb-1"><ShieldCheck className="h-4 w-4 text-[#F05A28]" /><h3 className="font-semibold text-slate-900">{t("Confidentialité et visibilité des saisies")}</h3></div>
      <p className="text-xs text-slate-500 mb-5">{t("Chaque saisie est nominative et horodatée (voir le journal d'audit). Ces règles déterminent qui peut consulter les données des autres.")}</p>

      <div className="flex items-start justify-between gap-4 border border-slate-200 rounded-md p-4 mb-5">
        <div>
          <p className="text-sm font-semibold text-slate-800">{t("Visibilité d'équipe par zone de santé")}</p>
          <p className="text-xs text-slate-500 mt-1">{t("Activée : les agents d'une même zone de santé voient les saisies de leur zone (lecture seule, seul l'auteur peut modifier) et partagent un rapport hebdomadaire de zone. Désactivée : chacun ne voit que ses propres saisies.")}</p>
        </div>
        <Switch checked={!!form.zone_team} onCheckedChange={(v) => setForm({ ...form, zone_team: v })} data-testid="vis-zone-team" />
      </div>

      <p className="text-sm font-semibold text-slate-800 mb-1">{t("Piliers en accès restreint")}</p>
      <p className="text-xs text-slate-500 mb-3">{t("Les données d'un pilier restreint ne sont visibles que par ses propres agents, l'Analyste SI et l'Administrateur (le superviseur provincial et le gestionnaire de programme en sont exclus).")}</p>
      <div className="grid sm:grid-cols-2 gap-2 mb-5">
        {data.pillars.map((p) => (
          <label key={p.code} className="flex items-center gap-2.5 text-sm cursor-pointer text-slate-700 border border-slate-200 rounded-lg px-3 py-2">
            <input type="checkbox" className="h-4 w-4 accent-[#F05A28]" checked={form.restricted_pillars.includes(p.code)}
              onChange={() => toggle(p.code)} data-testid={`vis-pillar-${p.code}`} />
            <span>{t(p.label)}</span>
          </label>
        ))}
      </div>
      <Button className="bg-[#F05A28] hover:bg-[#D84A1B]" onClick={save} disabled={busy} data-testid="vis-save">{t("Enregistrer les règles")}</Button>
    </div>
  );
}

function BulkUsersPanel({ onClose }) {
  const qc = useQueryClient();
  const { t } = useLang();
  const [lines, setLines] = useState("");
  const [password, setPassword] = useState("Demo@FHI360");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/users/bulk", { lines, password });
      setRes(r.data);
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success(`${r.data.created_count} ${t("compte(s) créé(s)")}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 mb-4" data-testid="bulk-users-panel">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="font-semibold text-slate-900">{t("Créer plusieurs comptes nominatifs")}</h3>
          <p className="text-xs text-slate-500 mt-1">{t("Pour créer les comptes des participants, renseignez pour chacun : nom complet ; adresse e-mail professionnelle ; rôle ; province d'affectation ; zone de santé (facultative). Une personne par ligne.")}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="bulk-users-close">{t("Fermer")}</Button>
      </div>
      <div className="mb-3 text-[11px] text-slate-500 font-mono bg-slate-50 border border-slate-200 rounded-lg p-2.5 leading-relaxed">
        field_epi · lab_tech · rcce_agent · eds_agent · ipc_agent · case_mgmt_agent · provincial_supervisor · si_analyst · program_manager · admin
      </div>
      <Textarea rows={6} value={lines} onChange={(e) => setLines(e.target.value)} data-testid="bulk-users-lines"
        placeholder={"Dr. Jean Kasongo ; jean.kasongo@fhi360.org ; field_epi ; Nord-Kivu ; Beni\nMarie Lumu ; marie.lumu@fhi360.org ; si_analyst"} />
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mt-3">
        <div className="sm:w-64">
          <Label className="text-xs">{t("Mot de passe provisoire commun")}</Label>
          <Input value={password} onChange={(e) => setPassword(e.target.value)} data-testid="bulk-users-password" />
        </div>
        <Button className="bg-[#F05A28] hover:bg-[#D84A1B]" onClick={submit} disabled={busy || !lines.trim()} data-testid="bulk-users-submit">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1" />}{t("Créer les comptes")}
        </Button>
      </div>
      {res && (
        <div className="mt-4 text-sm" data-testid="bulk-users-result">
          <p className="font-semibold text-emerald-700">{res.created_count} {t("compte(s) créé(s)")}</p>
          <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
            {res.created.map((c) => <li key={c.email}>{c.name} · {c.email} · {t(ROLE_LABELS[c.role] || c.role)}{c.province ? ` · ${c.province}` : ""}{c.health_zone ? ` / ${c.health_zone}` : ""}</li>)}
          </ul>
          {res.skipped_count > 0 && (
            <>
              <p className="font-semibold text-amber-700 mt-2">{res.skipped_count} {t("ligne(s) ignorée(s)")}</p>
              <ul className="mt-1 space-y-0.5 text-xs text-amber-700">
                {res.skipped.map((s, i) => <li key={i}>{s.line} : {s.reason}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PowerBiPanel() {
  const { t } = useLang();
  const { data, refetch } = useQuery({ queryKey: ["powerbi"], queryFn: async () => (await api.get("/powerbi/config")).data });
  const [ds, setDs] = useState("weekly_indicators");
  const [busy, setBusy] = useState(false);
  if (!data) return null;

  const rotate = async () => {
    setBusy(true);
    try {
      await api.post("/powerbi/rotate-token");
      await refetch();
      toast.success(t("Nouveau jeton généré. Mettez à jour l'URL dans PowerBI."));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const feedUrl = data.token
    ? `${data.feed_base}?dataset=${ds}&token=${data.token}`
    : `${data.feed_base}?dataset=${ds}&token=<JETON>`;

  const template = (fmt) => {
    const token = localStorage.getItem("token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/powerbi/template?fmt=${fmt}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) { toast.error(t("Générez d'abord un jeton PowerBI.")); return null; }
        return r.blob();
      })
      .then((b) => {
        if (!b) return;
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = fmt === "pbids" ? "FHI360_Surveillance.pbids" : "FHI360_Surveillance_PowerQuery.m";
        a.click();
        URL.revokeObjectURL(url);
        toast.success(t("Modèle téléchargé."));
      });
  };

  const download = (fmt) => {
    const token = localStorage.getItem("token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/powerbi/export?dataset=${ds}&fmt=${fmt}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = `powerbi_${ds}.${fmt === "xlsx" ? "xlsx" : "csv"}`; a.click();
        URL.revokeObjectURL(url);
        toast.success(t("Export démarré."));
      });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6" data-testid="powerbi-panel">
      <div className="flex items-center gap-2 mb-1"><BarChart3 className="h-4 w-4 text-[#F05A28]" /><h3 className="font-semibold text-slate-900">{t("Connexion PowerBI")}</h3></div>
      <p className="text-xs text-slate-500 mb-5">{t("Flux web sécurisé par jeton (PowerBI › Obtenir les données › Web) et export de fichier pour un import manuel.")}</p>

      <Label className="text-xs">{t("Jeu de données")}</Label>
      <select value={ds} onChange={(e) => setDs(e.target.value)} data-testid="pbi-dataset"
        className="mt-1 mb-4 w-full border border-slate-300 rounded-lg h-10 px-3 text-sm bg-white">
        {data.datasets.map((d) => <option key={d.key} value={d.key}>{t(d.label)}</option>)}
      </select>

      <Label className="text-xs">{t("URL du flux à coller dans PowerBI")}</Label>
      <div className="flex gap-2 mt-1 mb-2">
        <Input readOnly value={feedUrl} className="font-mono text-xs" data-testid="pbi-url" />
        <Button variant="outline" onClick={() => { navigator.clipboard.writeText(feedUrl); toast.success(t("URL copiée.")); }} data-testid="pbi-copy">{t("Copier")}</Button>
      </div>
      {!data.token_set && <p className="text-xs text-amber-700 mb-3">{t("Aucun jeton actif : générez-en un pour activer le flux.")}</p>}

      <div className="flex flex-wrap gap-2 mt-4">
        <Button className="bg-[#F05A28] hover:bg-[#D84A1B]" onClick={rotate} disabled={busy} data-testid="pbi-rotate">
          <RefreshCw className="h-4 w-4 mr-1" /> {t(data.token_set ? "Régénérer le jeton" : "Générer le jeton")}
        </Button>
        <Button variant="outline" onClick={() => download("xlsx")} data-testid="pbi-xlsx"><Download className="h-4 w-4 mr-1" /> Excel</Button>
        <Button variant="outline" onClick={() => download("csv")} data-testid="pbi-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
      </div>

      <div className="mt-5 border-t border-slate-200 pt-4">
        <p className="text-sm font-semibold text-slate-800 mb-1">{t("Modèle prêt à l'emploi pour l'équipe SI")}</p>
        <p className="text-xs text-slate-500 mb-3">{t("Le fichier .pbids ouvre PowerBI Desktop déjà connecté au flux. Le script Power Query contient les trois requêtes (saisies, indicateurs, complétude) à coller tel quel.")}</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => template("pbids")} data-testid="pbi-tpl-pbids"><Download className="h-4 w-4 mr-1" /> {t("Fichier PowerBI (.pbids)")}</Button>
          <Button variant="outline" onClick={() => template("m")} data-testid="pbi-tpl-m"><Download className="h-4 w-4 mr-1" /> {t("Script Power Query (.m)")}</Button>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-4">{t("Le jeton donne accès en lecture aux données agrégées (aucune note confidentielle n'est exposée). Régénérez-le si l'URL a été partagée par erreur.")}</p>
    </div>
  );
}

function Dhis2Panel() {
  const qc = useQueryClient();
  const { t } = useLang();
  const { data } = useQuery({ queryKey: ["dhis2"], queryFn: async () => (await api.get("/dhis2/config")).data });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const now = new Date();
  const [exp, setExp] = useState({ year: now.getFullYear(), week: 1 });
  React.useEffect(() => {
    if (data && !form) setForm({ server_url: data.server_url || "", dataset_id: data.dataset_id || "", org_units: data.org_units || {}, data_elements: data.data_elements || {} });
  }, [data]);
  if (!form) return null;

  const save = async () => {
    setBusy(true);
    try { await api.put("/dhis2/config", form); toast.success(t("Configuration DHIS2 enregistrée.")); qc.invalidateQueries({ queryKey: ["dhis2"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const runExport = async () => {
    setBusy(true);
    try {
      const res = await api.get("/dhis2/export", { params: { year: Number(exp.year), week: Number(exp.week) } });
      const blob = new Blob([JSON.stringify(res.data.payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `dhis2_export_${res.data.period}.json`; a.click();
      URL.revokeObjectURL(url);
      const un = res.data.unmapped?.length || 0;
      if (un) toast.warning(t("Export simulé généré, mais") + ` ${un} ` + t("valeur(s) non mappée(s)."));
      else toast.success(t("Export simulé généré avec succès."));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5" data-testid="dhis2-panel">
      <div className="flex items-center gap-2 mb-1"><Database className="h-4 w-4 text-[#F05A28]" /><h3 className="font-semibold text-slate-900">DHIS2</h3></div>
      <p className="text-xs text-slate-500 mb-4">{t("Mappage des unités d'organisation et éléments de données, puis export agrégé par semaine (mode simulation).")}</p>
      <div className="space-y-3">
        <div><Label className="text-xs">{t("URL du serveur DHIS2")}</Label><Input placeholder="https://dhis2.exemple.org" value={form.server_url} onChange={(e) => setForm({ ...form, server_url: e.target.value })} data-testid="dhis2-url" /></div>
        <div><Label className="text-xs">{t("Identifiant du jeu de données (dataSet)")}</Label><Input placeholder="dataSet UID" value={form.dataset_id} onChange={(e) => setForm({ ...form, dataset_id: e.target.value })} data-testid="dhis2-dataset" /></div>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div>
          <p className="text-xs font-semibold text-slate-600 uppercase mb-2">{t("Unités d'organisation (province → orgUnit)")}</p>
          <div className="space-y-2">{(data.provinces || []).map((p) => (
            <div key={p} className="flex items-center gap-2">
              <span className="text-xs w-28 shrink-0 text-slate-500 truncate">{p}</span>
              <Input className="h-8 text-xs" placeholder="orgUnit UID" value={form.org_units[p] || ""} data-testid={`dhis2-ou-${p}`}
                onChange={(e) => setForm({ ...form, org_units: { ...form.org_units, [p]: e.target.value } })} />
            </div>
          ))}</div>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-600 uppercase mb-2">{t("Éléments de données (pilier → dataElement)")}</p>
          <div className="space-y-2">{(data.pillars || []).map((pl) => (
            <div key={pl.code} className="flex items-center gap-2">
              <span className="text-xs w-28 shrink-0 text-slate-500 truncate">{t(pl.name)}</span>
              <Input className="h-8 text-xs" placeholder="dataElement UID" value={form.data_elements[pl.code] || ""} data-testid={`dhis2-de-${pl.code}`}
                onChange={(e) => setForm({ ...form, data_elements: { ...form.data_elements, [pl.code]: e.target.value } })} />
            </div>
          ))}</div>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2 mt-5 pt-4 border-t border-slate-100">
        <Button className="bg-[#F05A28] hover:bg-[#D84A1B]" onClick={save} disabled={busy} data-testid="dhis2-save">{t("Enregistrer le mappage")}</Button>
        <div><Label className="text-[11px] text-slate-500">{t("Année")}</Label><Input className="h-9 w-24" type="number" value={exp.year} onChange={(e) => setExp({ ...exp, year: e.target.value })} data-testid="dhis2-year" /></div>
        <div><Label className="text-[11px] text-slate-500">{t("Semaine")}</Label><Input className="h-9 w-20" type="number" min="1" max="53" value={exp.week} onChange={(e) => setExp({ ...exp, week: e.target.value })} data-testid="dhis2-week" /></div>
        <Button variant="outline" onClick={runExport} disabled={busy} data-testid="dhis2-export"><Download className="h-4 w-4 mr-1" /> {t("Exporter (simulation)")}</Button>
      </div>
    </div>
  );
}

const IntegrationCard = ({ title, subtitle, fields }) => {
  const { t } = useLang();
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
      <div className="flex items-center gap-2 mb-1"><Plug className="h-4 w-4 text-[#F05A28]" /><h3 className="font-semibold text-slate-900">{title}</h3></div>
      <p className="text-xs text-slate-500 mb-4">{subtitle}</p>
      <div className="space-y-3">{fields.map((f) => (
        <div key={f}><Label className="text-xs">{t(f)}</Label><Input placeholder={t(f)} disabled /></div>
      ))}</div>
      <Button variant="outline" className="w-full mt-4" disabled>{t("Configuration à venir")}</Button>
    </div>
  );
};

function GeoManager({ geo }) {
  const qc = useQueryClient();
  const { t } = useLang();
  const [newProv, setNewProv] = useState("");
  const [terrInput, setTerrInput] = useState({});
  const [zoneInput, setZoneInput] = useState({});
  const [busy, setBusy] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ["config"] });

  const add = async (payload) => {
    setBusy(true);
    try { await api.post("/geo/add", payload); toast.success(t("Enregistré.")); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (payload) => {
    if (!window.confirm(t("Confirmer la suppression ?"))) return;
    setBusy(true);
    try { await api.post("/geo/delete", payload); toast.success(t("Supprimé.")); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const zk = (p, terr) => `${p}|${terr}`;
  const setZone = (p, terr, patch) => setZoneInput((s) => ({ ...s, [zk(p, terr)]: { ...(s[zk(p, terr)] || {}), ...patch } }));

  return (
    <div data-testid="geo-manager">
      <p className="text-xs text-slate-500 mb-4">{t("Les listes géographiques sont modifiables ici (ajout / suppression). Les suppressions sont bloquées si des utilisateurs ou rapports y sont rattachés.")}</p>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4 mb-5 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[220px]"><Label className="text-xs">{t("Nom de la province")}</Label>
          <Input value={newProv} onChange={(e) => setNewProv(e.target.value)} placeholder={t("Nom de la province")} data-testid="geo-new-province" /></div>
        <Button className="bg-[#F05A28] hover:bg-[#D84A1B]" disabled={busy || !newProv.trim()} data-testid="geo-add-province"
          onClick={async () => { await add({ level: "province", name: newProv.trim() }); setNewProv(""); }}>
          <Plus className="h-4 w-4 mr-1" /> {t("Ajouter la province")}</Button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {Object.entries(geo).map(([p, pv]) => (
          <div key={p} className="bg-white border border-slate-200 rounded-md shadow-sm p-5" data-testid={`geo-province-${p}`}>
            <div className="flex items-center justify-between">
              <div><h3 className="font-semibold text-slate-900">{p}</h3><p className="text-xs text-slate-500">{t("Province")}</p></div>
              <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50" disabled={busy}
                onClick={() => del({ level: "province", name: p })} data-testid={`geo-del-province-${p}`}><Trash2 className="h-4 w-4" /></Button>
            </div>

            <div className="mt-3 space-y-3">
              {Object.entries(pv.territories || {}).map(([terr, zs]) => (
                <div key={terr} className="border-t border-slate-100 pt-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-700">{terr}</p>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50" disabled={busy}
                      onClick={() => del({ level: "territory", province: p, name: terr })} data-testid={`geo-del-territory-${terr}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {zs.map((z) => (
                      <span key={z} className="text-xs bg-slate-100 pl-2 pr-1 py-0.5 rounded inline-flex items-center gap-1">
                        {z}
                        <button className="text-slate-400 hover:text-red-600" disabled={busy}
                          onClick={() => del({ level: "zone", province: p, territory: terr, name: z })} data-testid={`geo-del-zone-${z}`}>×</button>
                      </span>
                    ))}
                  </div>
                  <div className="mt-2 grid grid-cols-6 gap-1.5">
                    <Input className="col-span-3 h-8 text-xs" placeholder={t("Zone de santé")} value={zoneInput[zk(p, terr)]?.name || ""}
                      onChange={(e) => setZone(p, terr, { name: e.target.value })} data-testid={`geo-zone-name-${terr}`} />
                    <Input className="h-8 text-xs" placeholder="Lat" value={zoneInput[zk(p, terr)]?.lat || ""} onChange={(e) => setZone(p, terr, { lat: e.target.value })} />
                    <Input className="h-8 text-xs" placeholder="Lng" value={zoneInput[zk(p, terr)]?.lng || ""} onChange={(e) => setZone(p, terr, { lng: e.target.value })} />
                    <Button size="icon" variant="outline" className="h-8 w-8" disabled={busy || !(zoneInput[zk(p, terr)]?.name || "").trim()} data-testid={`geo-add-zone-${terr}`}
                      onClick={async () => { const zi = zoneInput[zk(p, terr)] || {}; await add({ level: "zone", province: p, territory: terr, name: zi.name.trim(), lat: zi.lat, lng: zi.lng }); setZone(p, terr, { name: "", lat: "", lng: "" }); }}>
                      <Plus className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 border-t border-slate-100 pt-3 flex gap-1.5">
              <Input className="h-8 text-xs" placeholder={t("Nom du territoire")} value={terrInput[p] || ""}
                onChange={(e) => setTerrInput((s) => ({ ...s, [p]: e.target.value }))} data-testid={`geo-terr-name-${p}`} />
              <Button size="sm" variant="outline" className="h-8" disabled={busy || !(terrInput[p] || "").trim()} data-testid={`geo-add-territory-${p}`}
                onClick={async () => { await add({ level: "territory", province: p, name: (terrInput[p] || "").trim() }); setTerrInput((s) => ({ ...s, [p]: "" })); }}>
                <Plus className="h-4 w-4 mr-1" /> {t("Ajouter un territoire")}</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DigestSchedule() {
  const qc = useQueryClient();
  const { t } = useLang();
  const { data: s } = useQuery({ queryKey: ["digest"], queryFn: async () => (await api.get("/digest/settings")).data });
  const [form, setForm] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (s && !form) setForm({ day: String(s.day ?? 0), hour: String(s.hour ?? 7) }); }, [s]);
  const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
  if (!form) return null;

  const save = async () => {
    setBusy(true);
    try { await api.put("/digest/settings", { day: Number(form.day), hour: Number(form.hour) }); toast.success(t("Planification enregistrée.")); qc.invalidateQueries({ queryKey: ["digest"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendNow = async () => {
    setBusy(true);
    try { await api.post("/digest/send-now"); toast.success(t("Résumé en cours d'envoi aux gestionnaires.")); qc.invalidateQueries({ queryKey: ["email-logs"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5" data-testid="digest-schedule-card">
      <div className="flex items-center gap-2 mb-1"><CalendarClock className="h-4 w-4 text-[#0284C7]" /><h3 className="font-semibold text-slate-900">{t("Planification du résumé hebdomadaire")}</h3></div>
      <p className="text-xs text-slate-500 mb-4">{t("Choisissez le jour et l'heure d'envoi automatique du résumé aux gestionnaires (fuseau horaire de Kinshasa).")}</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">{t("Jour d'envoi")}</Label>
          <Select value={form.day} onValueChange={(v) => setForm({ ...form, day: v })}>
            <SelectTrigger data-testid="digest-day"><SelectValue /></SelectTrigger>
            <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{t(d)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{t("Heure (Kinshasa)")}</Label>
          <Select value={form.hour} onValueChange={(v) => setForm({ ...form, hour: v })}>
            <SelectTrigger data-testid="digest-hour"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-60">{Array.from({ length: 24 }).map((_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button className="bg-[#F05A28] hover:bg-[#D84A1B]" onClick={save} disabled={busy} data-testid="digest-save">{t("Enregistrer")}</Button>
        <Button variant="outline" onClick={sendNow} disabled={busy} data-testid="digest-send-now"><Send className="h-4 w-4 mr-1" /> {t("Envoyer le résumé maintenant")}</Button>
      </div>
    </div>
  );
}

function EmailTestCard() {  const { t } = useLang();
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post("/email/test", { to }); toast.success(t("E-mail de test envoyé.")); setTo(""); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5" data-testid="email-test-card">
      <div className="flex items-center gap-2 mb-1"><Mail className="h-4 w-4 text-[#F05A28]" /><h3 className="font-semibold text-slate-900">{t("Test e-mail")}</h3></div>
      <p className="text-xs text-slate-500 mb-4">{t("Envoyer un e-mail de test pour vérifier la configuration des notifications (Resend).")}</p>
      <div className="space-y-3">
        <div><Label className="text-xs">{t("Adresse e-mail du destinataire")}</Label><Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="nom@exemple.org" data-testid="email-test-to" /></div>
      </div>
      <Button className="w-full mt-4 bg-[#F05A28] hover:bg-[#D84A1B]" disabled={busy || !to} onClick={send} data-testid="email-test-send">{t("Envoyer un test")}</Button>
    </div>
  );
}

function KoboPanel() {
  const qc = useQueryClient();
  const { t } = useLang();
  const { data: s } = useQuery({ queryKey: ["kobo"], queryFn: async () => (await api.get("/kobo/settings")).data });
  const { data: logs } = useQuery({ queryKey: ["kobo-logs"], queryFn: async () => (await api.get("/kobo/logs")).data });
  const [form, setForm] = React.useState(null);
  const [importing, setImporting] = React.useState(false);
  React.useEffect(() => {
    if (s && !form) setForm({ server_url: s.server_url || "https://kf.kobotoolbox.org", asset_uid: s.asset_uid || "", api_token: "" });
  }, [s]);

  const save = async () => {
    try { await api.put("/kobo/settings", form); toast.success(t("Configuration Kobo enregistrée.")); qc.invalidateQueries({ queryKey: ["kobo"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const runImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/kobo/import");
      toast.success(`${data.inserted} ${t("insérés")} · ${data.duplicates} ${t("doublons")} · ${data.invalid} ${t("invalides")}`);
      qc.invalidateQueries({ queryKey: ["kobo-logs"] });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };
  if (!form) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5" data-testid="kobo-panel">
      <div className="flex items-center gap-2 mb-1">
        <Plug className="h-4 w-4 text-[#0284C7]" />
        <h3 className="font-semibold text-slate-900">KoboToolbox</h3>
        {s?.configured && <span className="text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">{t("Configuré")}</span>}
      </div>
      <p className="text-xs text-slate-500 mb-4">KoboCollect › KoboToolbox › API › Base de surveillance</p>
      <div className="space-y-3">
        <div><Label className="text-xs">{t("URL du serveur")}</Label><Input value={form.server_url} onChange={(e) => setForm({ ...form, server_url: e.target.value })} data-testid="kobo-url" /></div>
        <div><Label className="text-xs">Asset UID</Label><Input value={form.asset_uid} onChange={(e) => setForm({ ...form, asset_uid: e.target.value })} placeholder="aXXXXXXXXXXXXXXXX" data-testid="kobo-uid" /></div>
        <div><Label className="text-xs">{t("Jeton API")}</Label><Input type="password" value={form.api_token} onChange={(e) => setForm({ ...form, api_token: e.target.value })} placeholder="Token…" data-testid="kobo-token" /></div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button className="bg-[#F05A28] hover:bg-[#D84A1B]" onClick={save} data-testid="kobo-save">{t("Enregistrer")}</Button>
        <Button variant="outline" onClick={runImport} disabled={importing} data-testid="kobo-import">
          {importing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} {t("Importer maintenant")}
        </Button>
      </div>
      {logs?.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <p className="text-xs font-semibold text-slate-500 mb-2">{t("Journal d'import")}</p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {logs.slice(0, 8).map((l) => (
              <div key={l.id} className="text-xs flex items-center justify-between">
                <span className="text-slate-400">{l.started_at?.slice(0, 16).replace("T", " ")}</span>
                <span className={l.status === "completed" ? "text-emerald-600" : "text-red-600"}>
                  {l.status === "completed" ? `${l.inserted} ${t("insérés")} · ${l.duplicates} ${t("doublons")}` : (l.errors || []).join(", ")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
